import Check from "@mui/icons-material/Check";
import { Box, Card, Stack, Typography } from "@mui/material";

import CustomizeButton from "@/components/CustomizeButton";
import { makePlanName } from "@/components/subscription/customize/PricingBanner/helpers";
import formatBandwidth from "@/utils/formatBandwidth";
import getProxyPlanKey from "@/utils/getProxyPlanKey";
import { userFormatCurrency } from "@/utils/textUtils";
import PopularPlansSelector from "./PopularPlansSelector";
import ProductPlanHeader from "./ProductPlanHeader";
import { productOverviewCardSx } from "./ProductOverviewCard";
import { DashboardProduct } from "./useDashboardProducts";

/**
 * The canceled-product tab content (r-1776): the Product Overview card without
 * the usage overview — canceled plan details with Buy Again — followed by the
 * Popular Plans purchase flow without the outer outline border.
 */
export default function CanceledProductView({
  product,
}: {
  product: DashboardProduct;
}) {
  const plan = product.plan;
  if (!plan || product.type === "free") return null;

  const proxyPlanKey = getProxyPlanKey(plan);

  const features = [
    proxyPlanKey === "shared_residential"
      ? "Up to 80 million IPs / month"
      : `${plan.proxy_count.toLocaleString()} Proxies`,
    formatBandwidth(plan.bandwidth_limit),
  ];

  return (
    <Stack spacing={3} data-testid={`product-canceled-${product.type}`}>
      <Card sx={productOverviewCardSx}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
          <ProductPlanHeader
            icon={product.icon}
            title={product.title}
            exclusivity={makePlanName(proxyPlanKey)[1]}
            caption={`${userFormatCurrency(plan.monthly_price)}/month`}
            actions={
              <CustomizeButton
                variant="contained"
                color="primary"
                customizeData={{
                  source: "dashboard-buy-again",
                  proxyPlan: proxyPlanKey,
                  existingPlanId: null,
                }}
              >
                Buy Again
              </CustomizeButton>
            }
          />
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          spacing={2}
          sx={{ p: 3 }}
        >
          {features.map((feature) => (
            <Stack
              key={feature}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Check color="primary" fontSize="small" />
              <Typography variant="subtitle2" noWrap>
                {feature}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Card>
      <Card>
        <PopularPlansSelector planKey={product.type} />
      </Card>
    </Stack>
  );
}
